import { useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, List, Space, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useShipmentsTransition } from '@/api/generated/endpoints/shipments/shipments';
import type { BatchDetail, BatchShipmentOut } from '@/api/generated/model';
import { useHasRole } from '@/auth/useHasRole';
import { confirmAction } from '@/components/confirmAction';
import { problemMessage } from '@/i18n/problem';
import { invalidatePath } from '@/lib/queryCache';
import { qtyText } from '@/lib/units';

const DATETIME = 'DD.MM.YYYY HH:mm';

/**
 * Куда едет товар партии (ADR-0011 п. 5): резервы «Поступило на склад» и отгруженное.
 * По скану палета кладовщик отгружает отсюда — только после «Вы уверены?».
 */
export function BatchShipments({ batch }: { batch: BatchDetail }) {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const canShip = useHasRole('storekeeper');
  const [error, setError] = useState<string | null>(null);
  const transition = useShipmentsTransition();
  const uom = batch.product.base_uom;

  async function ship(s: BatchShipmentOut) {
    setError(null);
    const ok = await confirmAction(modal, {
      title: t('batchShip.confirmTitle', { number: s.number, client: s.client }),
      details: [
        t('batchShip.summary', { number: s.number, client: s.client }),
        ...(s.delivery_point ? [t('batchShip.point', { point: s.delivery_point })] : []),
        t('batchShip.fromBatch', { qty: qtyText(t, s.qty, uom), id: batch.id }),
        t('batchShip.whole'),
      ],
      okText: t('transition.confirm_shipped', { number: s.number }),
      cancelText: t('common.cancel'),
    });
    if (!ok) return;
    try {
      await transition.mutateAsync({
        shipmentId: s.id,
        data: { to: 'shipped', version: s.version },
      });
      void message.success(t('batchShip.done', { number: s.number }));
      await Promise.all([
        invalidatePath(queryClient, '/api/v1/batches'),
        invalidatePath(queryClient, '/api/v1/stock'),
        invalidatePath(queryClient, '/api/v1/shipments'),
      ]);
    } catch (e) {
      setError(problemMessage(e));
    }
  }

  return (
    <>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 8 }} />}
      <List
        size="small"
        dataSource={batch.shipments}
        locale={{ emptyText: t('batchShip.empty') }}
        renderItem={(s) => (
          <List.Item
            actions={
              s.status === 'at_warehouse' && canShip
                ? [
                    <Button
                      key="ship"
                      type="primary"
                      loading={transition.isPending && transition.variables.shipmentId === s.id}
                      onClick={() => void ship(s)}
                    >
                      {t('transition.shipped')}
                    </Button>,
                  ]
                : []
            }
          >
            <Space direction="vertical" size={0}>
              <Space wrap>
                <Link to={`/shipments/${s.id}`}>{s.number}</Link>
                <Tag>{t(`status.shipment.${s.status}.label`)}</Tag>
                <span>{s.client}</span>
                {s.delivery_point && (
                  <Typography.Text type="secondary">{s.delivery_point}</Typography.Text>
                )}
              </Space>
              <Typography.Text type="secondary">
                {t('batchShip.qty', { qty: qtyText(t, s.qty, uom) })}
                {s.shipped_at && ` · ${dayjs(s.shipped_at).format(DATETIME)}`}
              </Typography.Text>
            </Space>
          </List.Item>
        )}
      />
    </>
  );
}
